import { useState, useEffect } from 'react';
import { getSupportTicket, replyToTicket, closeTicket } from '../../api/supportTicketApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';

const statusColors = {
  open: '#ffaa44',
  in_progress: '#66aaff',
  resolved: '#66ff99',
  closed: '#888',
};

const ViewSupportTicketModal = ({ ticket: initialTicket, onClose }) => {
  const [ticket, setTicket] = useState(initialTicket);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    getSupportTicket(initialTicket._id)
      .then((res) => {
        setTicket(res.data.ticket);
        setMessages(res.data.messages);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load ticket'))
      .finally(() => setLoading(false));
  }, [initialTicket._id]);

  const handleReply = async () => {
    if (!replyText.trim()) return;

    setReplying(true);
    setError('');
    try {
      await replyToTicket(ticket._id, { message: replyText });
      setReplyText('');
      // Reload ticket
      const res = await getSupportTicket(ticket._id);
      setTicket(res.data.ticket);
      setMessages(res.data.messages);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;

    setClosing(true);
    setError('');
    try {
      await closeTicket(ticket._id, { reason: 'User closed' });
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to close ticket');
    } finally {
      setClosing(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        overflow: 'auto',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#111',
          border: '1px solid rgba(0,229,255,0.2)',
          borderRadius: 12,
          maxWidth: 700,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ borderBottom: '1px solid rgba(0,229,255,0.1)', padding: '20px', background: 'rgba(0,229,255,0.03)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: cyan, fontSize: 11, fontWeight: 600, fontFamily: 'monospace', marginBottom: 4 }}>
                {ticket.ticketId}
              </div>
              <h4 style={{ color: '#fff', fontWeight: 700, margin: 0, marginBottom: 4 }}>{ticket.title}</h4>
              <div style={{ color: '#888', fontSize: 12 }}>{ticket.description}</div>
            </div>
            <span
              style={{
                background: `${statusColors[ticket.status]}20`,
                color: statusColors[ticket.status],
                border: `1px solid ${statusColors[ticket.status]}40`,
                borderRadius: 6,
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
              }}
            >
              {ticket.status.replace('_', ' ')}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 20, fontSize: 11, color: '#555' }}>
            <div>Created: {fmtIST(ticket.createdAt)}</div>
            <div>Category: <span style={{ color: '#888' }}>{ticket.category}</span></div>
            <div>Priority: <span style={{ color: '#888', textTransform: 'capitalize' }}>{ticket.priority}</span></div>
          </div>
        </div>

        {/* Messages */}
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#555' }}>Loading...</div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.length === 0 ? (
              <div style={{ color: '#555', textAlign: 'center', padding: 20 }}>No messages yet</div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg._id}
                  style={{
                    background: msg.senderType === 'admin' ? 'rgba(102,255,153,0.08)' : 'rgba(0,229,255,0.08)',
                    border: `1px solid ${msg.senderType === 'admin' ? 'rgba(102,255,153,0.2)' : 'rgba(0,229,255,0.2)'}`,
                    borderRadius: 8,
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: msg.senderType === 'admin' ? '#66ff99' : cyan }}>
                      {msg.senderId.name} {msg.senderType === 'admin' && <span style={{ fontSize: 10, color: '#666' }}>(Admin)</span>}
                    </div>
                    <div style={{ fontSize: 10, color: '#555' }}>{fmtIST(msg.createdAt)}</div>
                  </div>
                  <div style={{ color: '#ccc', fontSize: 13, lineHeight: '1.5' }}>{msg.message}</div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Reply & Close */}
        {ticket.status !== 'closed' && (
          <div style={{ borderTop: '1px solid rgba(0,229,255,0.1)', padding: '16px' }}>
            {error && (
              <div style={{ background: 'rgba(255,100,100,0.1)', border: '1px solid rgba(255,100,100,0.3)', color: '#ff6666', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                rows="3"
                disabled={replying}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(0,229,255,0.2)',
                  borderRadius: 8,
                  padding: '10px 14px',
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleReply}
                disabled={replying || !replyText.trim()}
                style={{
                  flex: 1,
                  background: replying || !replyText.trim() ? 'rgba(0,229,255,0.3)' : cyan,
                  border: 'none',
                  color: '#000',
                  borderRadius: 8,
                  padding: '8px',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: replying || !replyText.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {replying ? 'Sending...' : 'Send Reply'}
              </button>
              <button
                onClick={handleClose}
                disabled={closing}
                style={{
                  background: 'rgba(255,100,100,0.1)',
                  border: '1px solid rgba(255,100,100,0.3)',
                  color: '#ff6666',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontWeight: 600,
                  fontSize: 12,
                  cursor: closing ? 'not-allowed' : 'pointer',
                }}
              >
                {closing ? 'Closing...' : 'Close Ticket'}
              </button>
            </div>
          </div>
        )}

        {/* Closed Message */}
        {ticket.status === 'closed' && (
          <div style={{ borderTop: '1px solid rgba(0,229,255,0.1)', padding: '16px', background: 'rgba(200,50,50,0.05)', color: '#ff6666', textAlign: 'center', fontSize: 12 }}>
            This ticket is closed. Contact support to reopen it.
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewSupportTicketModal;
